import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Alert
} from '@mui/material';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorAlert from '../components/common/ErrorAlert';
import { fetchAnalyticsData } from '../store/slices/reportsSlice';

const Analytics = () => {
  const dispatch = useDispatch();
  const { analyticsData, loading, error } = useSelector((state) => state.reports);
  
  const [activeTab, setActiveTab] = useState(0);
  const [timeRange, setTimeRange] = useState('last_30_days');

  useEffect(() => {
    dispatch(fetchAnalyticsData({ timeRange }));
  }, [dispatch, timeRange]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
  };

  // Sample data - replace with actual data from store
  const leadTrendData = [
    { name: 'Jan', leads: 65, conversions: 28 },
    { name: 'Feb', leads: 75, conversions: 32 },
    { name: 'Mar', leads: 85, conversions: 38 },
    { name: 'Apr', leads: 70, conversions: 30 },
    { name: 'May', leads: 90, conversions: 42 },
    { name: 'Jun', leads: 95, conversions: 45 }
  ];

  const leadSourceData = [
    { name: 'Website', value: 40, color: '#8884d8' },
    { name: 'Social Media', value: 25, color: '#82ca9d' },
    { name: 'Referral', value: 20, color: '#ffc658' },
    { name: 'Email Campaign', value: 10, color: '#ff7300' },
    { name: 'Other', value: 5, color: '#00ff00' }
  ];

  const userPerformanceData = [
    { name: 'John Doe', leads: 45, conversions: 18 },
    { name: 'Jane Smith', leads: 38, conversions: 22 },
    { name: 'Mike Johnson', leads: 52, conversions: 15 },
    { name: 'Sarah Wilson', leads: 41, conversions: 19 },
    { name: 'Tom Brown', leads: 35, conversions: 16 }
  ];

  if (loading) {
    return <LoadingSpinner message="Loading analytics..." />;
  }

  const TabPanel = ({ children, value, index }) => {
    return (
      <div hidden={value !== index}>
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Advanced Analytics
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Deep insights into your CRM performance and trends.
      </Typography>

      {error && (
        <ErrorAlert 
          error={error} 
          onClose={() => dispatch({ type: 'reports/clearError' })} 
        />
      )}

      {/* Time Range Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={handleTimeRangeChange}
            >
              <MenuItem value="last_7_days">Last 7 Days</MenuItem>
              <MenuItem value="last_30_days">Last 30 Days</MenuItem>
              <MenuItem value="last_90_days">Last 90 Days</MenuItem>
              <MenuItem value="last_6_months">Last 6 Months</MenuItem>
              <MenuItem value="last_year">Last Year</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Analytics Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Lead Trends" />
            <Tab label="Lead Sources" />
            <Tab label="User Performance" />
            <Tab label="Revenue Analytics" />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <Typography variant="h6" gutterBottom>
            Lead Generation & Conversion Trends
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={leadTrendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="leads" stroke="#8884d8" strokeWidth={2} name="Total Leads" />
              <Line type="monotone" dataKey="conversions" stroke="#82ca9d" strokeWidth={2} name="Conversions" />
            </LineChart>
          </ResponsiveContainer>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Lead Sources Distribution
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={leadSourceData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {leadSourceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ mt: 2 }}>
                {leadSourceData.map((source, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: source.color,
                        mr: 1,
                        borderRadius: 1
                      }}
                    />
                    <Typography variant="body2">
                      {source.name}: {source.value}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" gutterBottom>
            User Performance Comparison
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={userPerformanceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="leads" fill="#8884d8" name="Total Leads" />
              <Bar dataKey="conversions" fill="#82ca9d" name="Conversions" />
            </BarChart>
          </ResponsiveContainer>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Typography variant="h6" gutterBottom>
            Revenue Analytics
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Revenue analytics features are coming soon. This will include revenue trends, 
            forecasting, and customer lifetime value analysis.
          </Alert>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default Analytics;